import { type } from 'arktype';

// =============================================================================
// Basic Dashboard Statistics
// =============================================================================

/**
 * Basic statistics response schema
 */
export const dashboardStatsSchema = type({
  usersCount: type('number').configure({
    description: 'Total number of user accounts in the tenant.',
    examples: [42],
  }),
  personsCount: type('number').configure({
    description: 'Total number of person records in the tenant.',
    examples: [120],
  }),
}).configure({ description: 'Top-level counts displayed on the admin dashboard landing widget.' });

export type DashboardStats = typeof dashboardStatsSchema.t;

// =============================================================================
// Audit Activity Models
// =============================================================================

/**
 * Audit activity data point (changes per day)
 */
export const auditActivityPointSchema = type({
  date: type('string').configure({
    description: 'ISO date (YYYY-MM-DD) of the bucket.',
    examples: ['2026-04-22'],
  }),
  count: type('number').configure({
    description: 'Number of audit log entries recorded on this day.',
    examples: [17],
  }),
}).configure({ description: 'Single data point in the audit activity timeline (one per day).' });

export type AuditActivityPoint = typeof auditActivityPointSchema.t;

/**
 * Audit activity response with timeline data
 */
export const auditActivityResponseSchema = type({
  data: auditActivityPointSchema.array(),
  total: type('number').configure({
    description: 'Total number of audit entries across the timeline window.',
    examples: [238],
  }),
}).configure({ description: 'Time-series response envelope for the audit activity widget.' });

export type AuditActivityResponse = typeof auditActivityResponseSchema.t;

/**
 * Recent audit change entry
 */
export const recentChangeSchema = type({
  id: type('number').configure({
    description: 'Monotonic audit log entry ID.',
    examples: [104231],
  }),
  schemaName: type('string').configure({
    description: 'Database schema that owns the audited table.',
    examples: ['core'],
  }),
  tableName: type('string').configure({
    description: 'Table name of the audited record.',
    examples: ['persons'],
  }),
  recordId: type('string').configure({
    description: 'Primary-key value of the audited record.',
    examples: ['per_abc123'],
  }),
  operation: type("'INSERT' | 'UPDATE' | 'DELETE'").configure({
    description: "Type of change: 'INSERT' created a record, 'UPDATE' modified one, 'DELETE' removed one.",
  }),
  'changedBy?': type('string | null').configure({
    description: 'User ID that performed the change. Optional and nullable for system-generated changes.',
    examples: ['usr_abc123'],
  }),
  'changedByName?': type('string | null').configure({
    description: 'Human-readable name of the user that performed the change. Optional and nullable.',
    examples: ['Anna Müller'],
  }),
  changedAt: type('string').configure({
    description: 'ISO 8601 timestamp of when the change was applied.',
    examples: ['2026-04-22T08:15:30.000Z'],
  }),
}).configure({ description: 'Lightweight audit entry used by the "recent changes" dashboard widget.' });

export type RecentChange = typeof recentChangeSchema.t;

/**
 * Recent changes response
 */
export const recentChangesResponseSchema = type({
  data: recentChangeSchema.array(),
}).configure({ description: 'Response envelope for the recent-changes dashboard widget.' });

export type RecentChangesResponse = typeof recentChangesResponseSchema.t;

/**
 * Operation breakdown entry
 */
export const operationBreakdownSchema = type({
  operation: type('string').configure({
    description: 'Operation kind (typically "INSERT", "UPDATE" or "DELETE").',
    examples: ['UPDATE'],
  }),
  count: type('number').configure({
    description: 'Number of audit entries with this operation in the current window.',
    examples: [85],
  }),
}).configure({ description: 'Single bar in the audit-operations breakdown chart.' });

export type OperationBreakdown = typeof operationBreakdownSchema.t;

/**
 * Schema breakdown entry
 */
export const schemaBreakdownSchema = type({
  schema: type('string').configure({
    description: 'Database schema name.',
    examples: ['core', 'planning'],
  }),
  count: type('number').configure({
    description: 'Number of audit entries belonging to this schema in the current window.',
    examples: [102],
  }),
}).configure({ description: 'Single bar in the audit-by-schema breakdown chart.' });

export type SchemaBreakdown = typeof schemaBreakdownSchema.t;

// =============================================================================
// Planning Dashboard Models — "Haben wir Engpässe" (dashboard-planning-overview-rework)
// =============================================================================

/**
 * Rolling 7-day KPI row.
 */
export const planningDashboardKpisSchema = type({
  openShifts7d: type('number').configure({
    description:
      'Number of under-staffed shift occurrences (one shift × one day) across the next 7 days. Equals the count of `open-shift` gap rows.',
    examples: [7],
  }),
  coverageRate7d: type('number').configure({
    description: 'Filled/required seat percentage across the next 7 days (capacity-weighted).',
    examples: [82],
  }),
  qualificationGaps7d: type('number').configure({
    description:
      'Sum over skills of the peak single-day headcount shortfall (required − available, floored at 0). Equals Σ(required − available) over `skill` gap rows.',
    examples: [2],
  }),
  spontaneousAbsences7d: type('number').configure({
    description: 'Number of team-member absences starting within the next 7 days (equals the count of `absence` gap rows).',
    examples: [1],
  }),
}).configure({ description: 'Top-line KPI row for the "Haben wir Engpässe" dashboard section.' });

export type PlanningDashboardKpis = typeof planningDashboardKpisSchema.t;

/**
 * One concrete, actionable bottleneck behind the KPI counts: an unfilled
 * shift-unit occurrence, a per-skill headcount shortfall for a team, or a team
 * member's absence starting in the window. In the same team scope as `kpis`
 * (see {@link planningDashboardMetricsSchema}), so each KPI is reconstructable
 * from these rows.
 */
export const gapIssueSchema = type({
  teamId: type('string').configure({ description: 'Team the gap belongs to.', examples: ['team_zug'] }),
  team: type('string').configure({ description: 'Team display name.', examples: ['Zug'] }),
  location: type('string').configure({
    description: "Shift-unit's location name, or '' when the unit has no location or the row isn't location-specific.",
    examples: ['Zug – Bahnhofplatz'],
  }),
  type: type("'open-shift' | 'skill' | 'absence'").configure({
    description:
      "'open-shift' is an unfilled shift-unit occurrence; 'skill' is a per-team headcount shortfall for a skill; 'absence' is a team member absent in the window.",
  }),
  date: type('string').configure({
    description: 'ISO YYYY-MM-DD. For skill rows, this is the window start date (a coverage-window snapshot, not a single occurrence).',
    examples: ['2026-07-24'],
  }),
  'shiftName?': type('string').configure({ description: 'Shift name (open-shift rows only).', examples: ['Schliessung'] }),
  'startTime?': type('string').configure({ description: 'HH:mm (open-shift rows only).', examples: ['22:00'] }),
  'endTime?': type('string').configure({ description: 'HH:mm (open-shift rows only).', examples: ['01:00'] }),
  'skillName?': type('string').configure({ description: 'Skill name (skill rows only).', examples: ['Grillstation'] }),
  'required?': type('number').configure({
    description: 'Display headcount need. Skill rows: peak single-day qualified need. Open-shift rows: unit capacity on that day.',
    examples: [2],
  }),
  'available?': type('number').configure({
    description: 'Skill rows: qualified people available. Open-shift rows: current fill on that day.',
    examples: [1],
  }),
  'personName?': type('string').configure({ description: 'Absent person display name (absence rows only).', examples: ['Julia Hoffmann'] }),
  severity: type("'warning' | 'critical'").configure({
    description: "'critical' for open-shift rows, 'warning' for skill and absence rows.",
  }),
}).configure({ description: 'A single itemized bottleneck row on the "Haben wir Engpässe" drill-down.' });

export type GapIssue = typeof gapIssueSchema.t;

/**
 * Combined planning dashboard metrics response.
 */
export const planningDashboardMetricsSchema = type({
  kpis: planningDashboardKpisSchema,
  gaps: gapIssueSchema.array(),
}).configure({ description: 'Aggregated "Haben wir Engpässe" dashboard payload: KPI row plus itemized, team-scoped gap rows.' });

export type PlanningDashboardMetrics = typeof planningDashboardMetricsSchema.t;

// =============================================================================
// Employee Dashboard Models (self-service view)
// =============================================================================

/**
 * A single shift assigned to the current employee
 */
export const myShiftItemSchema = type({
  id: type('string').configure({
    description: 'Shift assignment ID.',
    examples: ['shf_abc123'],
  }),
  date: type('string').configure({
    description: 'Shift date in ISO format (YYYY-MM-DD).',
    examples: ['2026-04-22'],
  }),
  shiftName: type('string').configure({
    description: 'Human-readable shift name.',
    examples: ['Early'],
  }),
  shiftTime: type('string').configure({
    description: 'Human-readable shift window (local time).',
    examples: ['06:00 - 14:00'],
  }),
  shiftType: type("'morning' | 'afternoon' | 'night' | 'oncall'").configure({
    description:
      "Classification of the shift window: 'morning' (early day), 'afternoon' (late day), 'night' (overnight), or 'oncall' (standby).",
  }),
  'teamName?': type('string | null').configure({
    description: 'Team the shift belongs to. Optional and nullable.',
    examples: ['Emergency'],
  }),
  // Fields needed by the ShiftActionDialog for swap/takeover marking
  'planInstanceId?': type('string').configure({
    description: 'ID of the plan instance the shift belongs to (needed for swap/takeover actions). Optional.',
    examples: ['pli_abc123'],
  }),
  'shiftUnitId?': type('string').configure({
    description: 'ID of the underlying shift unit (needed for swap/takeover actions). Optional.',
    examples: ['shu_abc123'],
  }),
  'swapStatus?': type("'looking_for_swap' | 'available_for_takeover' | null").configure({
    description:
      "Current swap marker on the shift: 'looking_for_swap' (owner wants someone to swap), 'available_for_takeover' (owner is giving it up), or null (no marker). Optional.",
  }),
  'employeeOverridable?': type('boolean').configure({
    description: 'Whether the assigned employee may edit the shift time/location for this unit. Optional.',
  }),
  'overrideStartTime?': type('string').configure({
    description: 'Employee-owned override start time for this assigned shift (HH:mm). Optional.',
    examples: ['07:30'],
  }),
  'overrideEndTime?': type('string').configure({
    description: 'Employee-owned override end time for this assigned shift (HH:mm). Optional.',
    examples: ['15:30'],
  }),
  'defaultLocationId?': type('string | null').configure({
    description: 'Default location configured on the shift unit. Optional and nullable.',
    examples: ['loc_abc123'],
  }),
  'overrideLocationId?': type('string | null').configure({
    description: 'Employee-owned location override for this assigned shift. Optional and nullable.',
    examples: ['loc_override_123'],
  }),
}).configure({ description: 'A single shift shown in the employee self-service dashboard.' });

export type MyShiftItem = typeof myShiftItemSchema.t;

/**
 * A work location assigned to the employee (from personLocations) with the
 * validity window. Returned with the employee dashboard so the inline shift
 * location override popover can list eligible locations per day.
 */
export const assignedLocationItemSchema = type({
  locationId: type('string').configure({
    description: 'Location ID.',
    examples: ['loc_abc123'],
  }),
  locationName: type('string').configure({
    description: 'Display name of the location.',
    examples: ['Zürich HQ'],
  }),
  type: type("'primary' | 'secondary' | 'home_office'").configure({
    description: 'Assignment type. Primary is the default; secondary and home_office are alternatives the employee can switch to.',
  }),
  'startDate?': type('string | null').configure({
    description: 'ISO date the assignment becomes valid (inclusive). Null when always valid.',
    examples: ['2026-01-01'],
  }),
  'endDate?': type('string | null').configure({
    description: 'ISO date the assignment expires (inclusive). Null when open-ended.',
    examples: ['2026-12-31'],
  }),
  'rrule?': type('string | null').configure({
    description: 'Optional RRULE restricting the assignment to recurring dates inside the validity window.',
  }),
}).configure({
  description: 'A single work location the employee may use, including the validity window.',
});

export type AssignedLocationItem = typeof assignedLocationItemSchema.t;

/**
 * Employee dashboard response: the logged-in employee's own shifts and stats
 */
export interface EmployeeDashboard {
  employeeName: string;
  roleName?: string | null;
  teamName?: string | null;
  weekStart: string;
  nextShift: MyShiftItem | null;
  weekShifts: MyShiftItem[];
  shiftsThisWeek: number;
  hoursThisWeek: number;
  assignedLocations: AssignedLocationItem[];
}
